// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CaretDown from "@west/XDL/Icons/CaretDown";
import { mergeWithCss, xClassNames } from "@west/XDL/Utilities";
import React, { FC, useState } from "react";

export interface HeaderClickableItemProps {
    variant?: "menu" | "cart";
    cartTotalCount?: number;
    isOpen?: boolean;
    extendedStyles?: HeaderClickableStyles;
}

export type HeaderClickableProps = HeaderClickableItemProps & ClickableProps;

type Props = HeaderClickableProps;

export interface HeaderClickableStyles {
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    title?: TypographyProps;
    cartIcon?: IconPresentationProps;
    cartTotalDisplay?: InjectableCss;
    arrowIcon?: IconPresentationProps;
}

export const headerClickableStyles: HeaderClickableStyles = {};

const HeaderClickable: FC<Props> = ({ variant, isOpen, cartTotalCount, children, extendedStyles, ...otherProps }) => {
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));
    const className = xClassNames.append([isOpen && "opened"], otherProps.className);

    if (variant === "cart") {
        return (
            <Clickable {...styles.clickable} {...otherProps} className={className}>
                {styles.cartIcon?.src && <Icon {...styles.cartIcon} />}
                {!!cartTotalCount && cartTotalCount > 0 ? (
                    <StyledWrapper {...styles.cartTotalDisplay}>{cartTotalCount}</StyledWrapper>
                ) : null}
                <Icon src={CaretDown} {...styles.arrowIcon} className="arrow-menu-icon" />
            </Clickable>
        );
    }

    return (
        <Clickable {...styles.clickable} {...otherProps} className={className}>
            {styles.icon?.src && <Icon {...styles.icon} />}
            <Typography {...styles.title}>{children}</Typography>
            <Icon src={CaretDown} {...styles.arrowIcon} className="arrow-menu-icon" />
        </Clickable>
    );
};

export default HeaderClickable;
